// Side effects for the payment phases, extracted from App.tsx so they can be
// tested with fakes. Each runner resolves after dispatching exactly one state
// event; telemetry is recorded along the way and never affects the outcome.
import { MAX_IM30_RAW_CHARS } from "@entradart/kiosk-shared/api";
import type { SaleOutcome } from "@entradart/kiosk-shared/bridge";
import type { Im30SaleResponse } from "@entradart/kiosk-shared/im30";
import type { ApiClient } from "../clients/api-client";
import { type BridgeClient, BridgeOutcomeError } from "../clients/bridge-client";
import type { EventLog } from "../clients/event-log";
import type { KioskEvent } from "../state/types";

export type SaleDeps = {
  bridge: Pick<BridgeClient, "sale">;
  api: Pick<ApiClient, "cancelOrder" | "confirmOrder">;
  // Must never throw (the real EventLog guarantees it), so telemetry can
  // never change the dispatched state.
  eventLog: Pick<EventLog, "record" | "flush">;
  // IM30 status messages collected since the sale started.
  statuses: () => { code: string; atMs: number }[];
  // Raw IM30 frames the bridge delivered beside this sale's outcome (newer
  // APKs only), or null. The bridge emits them before the sale promise
  // settles, so they are available when the outcome is recorded.
  frames?: () => SaleFrames | null;
  now?: () => number;
};

export type SaleFrames = { frames: unknown; requestId?: string };

// Frames (and their bridge requestId) to merge into the sale.outcome data;
// empty when the bridge sent none.
function framesData(deps: SaleDeps): Partial<SaleFrames> {
  const captured = deps.frames?.();
  return captured
    ? { frames: captured.frames, requestId: captured.requestId }
    : {};
}

export async function runSaleOnBridge(
  deps: SaleDeps,
  body: { orderId: number; amountCLP: number; ticketNumber: string },
  dispatch: (e: KioskEvent) => void
): Promise<void> {
  const now = deps.now ?? Date.now;
  const startedAt = now();
  deps.eventLog.record(
    "sale.started",
    { amountCLP: body.amountCLP, ticketNumber: body.ticketNumber },
    body.orderId
  );

  let outcome: SaleOutcome;
  try {
    outcome = await deps.bridge.sale({
      amountCLP: body.amountCLP,
      ticketNumber: body.ticketNumber,
      sendStatusMessages: true,
      requestVoucher: true,
    });
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    if (e instanceof BridgeOutcomeError) {
      // Terminal answered; verdict unreadable. The transport's parse_error has
      // no orderId, so record an order-tagged outcome staff can search by the
      // order number on screen. Do NOT claim "no charge".
      deps.eventLog.record(
        "sale.outcome",
        {
          outcome: { outcome: "unverified" },
          rawEvent: e.rawEvent,
          statuses: deps.statuses(),
          durationMs: now() - startedAt,
          ...framesData(deps),
        },
        body.orderId
      );
      dispatch({ type: "sale-unverified", detail: message });
      void deps.eventLog.flush();
      return;
    }
    // Link error: the verdict is unknown, so log it but never cancel.
    const linkError: SaleOutcome = { outcome: "link_error", detail: message };
    deps.eventLog.record(
      "sale.outcome",
      {
        outcome: linkError,
        statuses: deps.statuses(),
        durationMs: now() - startedAt,
        ...framesData(deps),
      },
      body.orderId
    );
    dispatch({ type: "sale-result", outcome: linkError });
    void deps.eventLog.flush();
    return;
  }

  deps.eventLog.record(
    "sale.outcome",
    {
      outcome,
      statuses: deps.statuses(),
      durationMs: now() - startedAt,
      ...framesData(deps),
    },
    body.orderId
  );

  dispatch({ type: "sale-result", outcome });

  if (outcome.outcome === "declined") {
    // Best-effort, fire-and-forget: the backend learns the decline and keeps
    // the raw frame. The visitor's screen must neither wait for nor depend on
    // this (the call is bounded by TELEMETRY_TIMEOUT_MS); dispatch already
    // happened.
    const report = {
      reason: "im30_decline" as const,
      // The route rejects a longer im30Raw with 400; a real 0210 is ~1 KB.
      im30Raw: outcome.response.raw.slice(0, MAX_IM30_RAW_CHARS),
    };
    // The backend logs order.cancelled / order.cancel_failed; the decline
    // itself (with the raw frame) is already in this sale's sale.outcome, so
    // a cancel that never reaches the backend loses nothing.
    try {
      deps.api.cancelOrder(body.orderId, report).catch(() => {
        // Best-effort; see above.
      });
    } catch {
      // A synchronous throw is ignored for the same reason.
    }
  }

  void deps.eventLog.flush();
}

export async function runConfirmOrder(
  deps: SaleDeps,
  orderId: number,
  im30: Im30SaleResponse,
  dispatch: (e: KioskEvent) => void
): Promise<void> {
  try {
    const res = await deps.api.confirmOrder(orderId, { im30 });
    dispatch({
      type: "confirm-result",
      tickets: res.tickets,
      voucher: res.voucher,
    });
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    const httpStatus = (e as { status?: number } | null)?.status ?? null;
    deps.eventLog.record("confirm.failed", { httpStatus, message, im30 }, orderId);
    void deps.eventLog.flush();
    dispatch({ type: "confirm-failed", reason: message });
  }
}
